import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import { ExportOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import {
    Table,
    Button,
    Typography,
    DatePicker,
    Row,
    Col,
    Card,
    Statistic,
    Empty,
    Tag,
    Input,
    Tooltip, // ==================== [BỔ SUNG] Bộ lọc khách hàng
} from 'antd';
import BaseSelect from '@/components/Elements/BaseSelect';
import moment from 'moment';
import { cleanObject } from '@/utils/helpers';
import {
    getStaffRevenueDetail,
    staffRevenueDetailExportExcel,
} from '@/redux/actions/statisticsActions';
import { checkPermission } from '@/utils/permission';
import { NotificationManager } from 'react-notifications';
import NumberFormat from 'react-number-format';
import CustomerInfoColumn from '@/components/customers/CustomerInfoColumn';

const { Title } = Typography;
const { RangePicker } = DatePicker;

// ==================== [BỔ SUNG] ====================
const { Search } = Input;
// ================== [KẾT THÚC BỔ SUNG] ==================

const routes = [
    {
        path: '/',
        breadcrumbName: 'Trang chủ',
    },
    {
        path: '/staff-revenue-report',
        breadcrumbName: 'Doanh số nhân viên chi tiết',
    },
];

export default function StaffRevenueReport() {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const authUser = useSelector((state) => state.auth.authUser);
    const users = useSelector((state) => state.config.users);

    const { summary, detail, pagination } = useSelector(
        (state) => state.statisticsReducer.staff_revenue_detail
    );

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingExport, setIsLoadingExport] = useState(false);

    // ==================== [BỔ SUNG] ====================
    // Giá trị đang nhập trong ô tìm kiếm khách hàng
    const [customerKeyword, setCustomerKeyword] = useState('');
    // ================== [KẾT THÚC BỔ SUNG] ==================

    const showStaffSelect =
        authUser?.role_code === 'ADMIN' ||
        authUser?.role_code === 'KETOAN';

    const allowExport = checkPermission(
        'statistics',
        'staff_revenue_detail_export'
    );

    const query = qs.parse(location.search.slice(1));

    const formatDate = (date) => {
        return moment.isMoment(date) && date.isValid()
            ? date.format('YYYY-MM-DD')
            : null;
    };

    /**
     * Kiểm tra URL đã đủ điều kiện để gọi API hay chưa.
     */
    const isQueryReady = () => {
        const currentQuery = qs.parse(location.search.slice(1));

        if (!currentQuery.start_date || !currentQuery.end_date) {
            return false;
        }

        if (showStaffSelect && !currentQuery.user_id) {
            return false;
        }

        if (!showStaffSelect && !authUser?.id) {
            return false;
        }

        return true;
    };

    /**
     * Thiết lập ngày mặc định và nhân viên mặc định.
     */
    useEffect(() => {
        const currentQuery = qs.parse(location.search.slice(1));

        let updated = false;

        const newQuery = {
            ...currentQuery,
        };

        // Ngày mặc định là tháng hiện tại
        if (!newQuery.start_date || !newQuery.end_date) {
            newQuery.start_date = moment()
                .startOf('month')
                .format('YYYY-MM-DD');

            newQuery.end_date = moment()
                .endOf('month')
                .format('YYYY-MM-DD');

            updated = true;
        }

        // Tự động gán nhân viên hiện tại
        const userId = authUser?.id
            ? String(authUser.id)
            : '';

        if (!newQuery.user_id && userId) {
            newQuery.user_id = userId;
            updated = true;
        }

        if (updated) {
            history.replace({
                pathname: location.pathname,
                search: qs.stringify(
                    cleanObject(newQuery)
                ),
            });
        }
    }, [
        location.pathname,
        location.search,
        authUser?.id,
        showStaffSelect,
        history,
    ]);

    // ==================== [BỔ SUNG] ====================
    /**
     * Đồng bộ giá trị ô tìm kiếm với customer_keyword trên URL.
     *
     * Ví dụ:
     * ?customer_keyword=Nguyen+Van+A
     */
    useEffect(() => {
        const currentQuery = qs.parse(
            location.search.slice(1)
        );

        setCustomerKeyword(
            currentQuery.customer_keyword
                ? String(currentQuery.customer_keyword)
                : ''
        );
    }, [location.search]);
    // ================== [KẾT THÚC BỔ SUNG] ==================

    /**
     * Gọi API lấy dữ liệu báo cáo.
     */
    const loadData = async () => {
        const currentQuery = qs.parse(
            location.search.slice(1)
        );

        const userId = showStaffSelect
            ? currentQuery.user_id
            : authUser?.id;

        if (!userId) {
            return;
        }

        setIsLoading(true);

        const params = {
            staff_id: userId,
            date_from: currentQuery.start_date,
            date_to: currentQuery.end_date,

            // ==================== [BỔ SUNG] ====================
            // Backend sẽ lọc theo:
            // customers.full_name
            // customers.code
            // customer_phones.phone
            customer_keyword: currentQuery.customer_keyword
                ? String(
                    currentQuery.customer_keyword
                ).trim()
                : undefined,
            // ================== [KẾT THÚC BỔ SUNG] ==================

            page: Number(currentQuery.page) || 1,
            per_page:
                Number(currentQuery.per_page) || 30,
        };

        try {
            await dispatch(
                getStaffRevenueDetail(params)
            );
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Tự động gọi lại API khi URL thay đổi.
     */
    useEffect(() => {
        if (!isQueryReady()) {
            return;
        }

        loadData();
    }, [location.search]);

    /**
     * Cập nhật các bộ lọc lên URL.
     */
    const onChangeFilter = (name, value) => {
        let nextQuery = {
            ...query,
        };

        /**
         * Bộ lọc nhân viên.
         */
        if (name === 'user_id') {
            if (value) {
                nextQuery.user_id = value;
            } else {
                delete nextQuery.user_id;
            }
        }

        /**
         * Bộ lọc ngày.
         */
        if (name === 'date') {
            const [start, end] = value || [];

            nextQuery.start_date =
                formatDate(start);

            nextQuery.end_date =
                formatDate(end);
        }

        // ==================== [BỔ SUNG] ====================
        /**
         * Bộ lọc khách hàng.
         *
         * Hỗ trợ tìm kiếm:
         * - Tên khách hàng
         * - Mã khách hàng
         * - Số điện thoại
         */
        if (name === 'customer_keyword') {
            const keyword = String(
                value || ''
            ).trim();

            if (keyword) {
                nextQuery.customer_keyword = keyword;
            } else {
                delete nextQuery.customer_keyword;
            }
        }
        // ================== [KẾT THÚC BỔ SUNG] ==================

        /**
         * Khi thay đổi bộ lọc phải quay về trang đầu.
         */
        delete nextQuery.page;

        nextQuery = cleanObject(nextQuery);

        history.push({
            pathname: location.pathname,
            search: qs.stringify(nextQuery),
        });
    };

    // ==================== [BỔ SUNG] ====================
    /**
     * Xử lý khi nhập hoặc xóa nội dung tìm kiếm.
     *
     * Khi người dùng xóa hết nội dung, bộ lọc trên URL
     * cũng được xóa ngay để tải lại toàn bộ dữ liệu.
     */
    const handleCustomerKeywordChange = (event) => {
        const value = event.target.value;

        setCustomerKeyword(value);

        if (
            !value &&
            query.customer_keyword
        ) {
            onChangeFilter(
                'customer_keyword',
                ''
            );
        }
    };

    /**
     * Áp dụng bộ lọc khi:
     * - Nhấn Enter
     * - Nhấn nút Lọc
     * - Nhấn biểu tượng tìm kiếm
     */
    const handleCustomerSearch = (value) => {
        onChangeFilter(
            'customer_keyword',
            value
        );
    };
    // ================== [KẾT THÚC BỔ SUNG] ==================

    /**
     * Xuất file Excel.
     */
    const startExcelExport = () => {
        const currentQuery = qs.parse(
            location.search.slice(1)
        );

        const userId = showStaffSelect
            ? currentQuery.user_id
            : authUser?.id;

        if (!userId) {
            return;
        }

        setIsLoadingExport(true);

        const params = {
            staff_id: userId,
            date_from: currentQuery.start_date,
            date_to: currentQuery.end_date,

            // ==================== [BỔ SUNG] ====================
            // Export theo đúng khách hàng đang được lọc
            customer_keyword: currentQuery.customer_keyword
                ? String(
                    currentQuery.customer_keyword
                ).trim()
                : undefined,
            // ================== [KẾT THÚC BỔ SUNG] ==================
        };

        dispatch(
            staffRevenueDetailExportExcel(params)
        )
            .then(() => {
                NotificationManager.success(
                    `Quá trình xuất file đang được thực hiện.
File kết quả sẽ được gửi vào email ${authUser?.user_email} trong ít phút.`,
                    'Thông báo',
                    9000
                );
            })
            .catch(() => {
                NotificationManager.error(
                    'Có lỗi xảy ra trong quá trình gửi yêu cầu xuất file. Vui lòng thử lại sau.',
                    'Lỗi',
                    9000
                );
            })
            .finally(() => {
                setIsLoadingExport(false);
            });
    };

    const columns = [
        {
            title: 'Tên khách hàng',
            key: 'customer_name',
            width: 300,
            render: (_, record) => {
                const customer = {
                    id: record?.customer_id,
                    code: record?.customer_code,
                    full_name: record?.customer_name
                }
                return (
                    <CustomerInfoColumn
                        record={customer}
                        nameField="full_name"          // field tuỳ module
                        idField="id"
                        codeField="code"
                        branchField="branch_name"
                        //medicalBookField="medical_book_number"
                        //conditionField="conditions"
                        sourceField="customer_source"
                        statusField="customer_status"
                        statusColorField="customer_status_color"
                    />
                )
            }
        },
        {
            title: 'Dịch vụ',
            dataIndex: 'service_name',
        },
        {
            title: 'Doanh số nhân viên',
            dataIndex: 'price',
            render: (value) =>
                Number(value || 0).toLocaleString(
                    'vi-VN'
                ),
        },
        {
            title: 'Doanh số & công nợ',
            key: 'revenue_summary',
            width: 220,
            align: 'right',
            render: (_, record) => {
                const revenue = Number(
                    record.revenue || 0
                );

                const paid = Number(
                    record.total_paid_amount || 0
                );

                const debt = Number(
                    record.debt || 0
                );

                if (revenue === 0) {
                    return <Tag />;
                }

                return (
                    <div
                        style={{
                            textAlign: 'right',
                            lineHeight: 1.4,
                        }}
                    >
                        {/* Tổng doanh số khách hàng */}
                        <div
                            style={{
                                fontWeight: 600,
                            }}
                        >
                            <NumberFormat
                                value={revenue}
                                displayType="text"
                                thousandSeparator
                                suffix=" đ"
                            />
                        </div>

                        {/* Tổng tiền đã thu */}
                        <div
                            style={{
                                fontSize: 12,
                                color: '#389e0d',
                            }}
                        >
                            Đã thu:{' '}
                            <NumberFormat
                                value={paid}
                                displayType="text"
                                thousandSeparator
                                suffix=" đ"
                            />
                        </div>

                        {/* Công nợ còn lại */}
                        {debt > 0 && (
                            <div
                                style={{
                                    fontSize: 12,
                                    color: '#cf1322',
                                }}
                            >
                                Còn nợ:{' '}
                                <NumberFormat
                                    value={debt}
                                    displayType="text"
                                    thousandSeparator
                                    suffix=" đ"
                                />
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Nhân viên tư vấn',
            dataIndex: 'consultants',
            key: 'consultants',
            width: 250,
            render: (_, record) => {
                const consultantList =
                    record.consultants || [];

                if (consultantList.length === 0) {
                    return null;
                }

                return (
                    <div
                        style={{
                            lineHeight: 1.3,
                        }}
                    >
                        {consultantList.map(
                            (item) => (
                                <div key={item.id}>
                                    <span
                                        style={{
                                            color: '#999',
                                        }}
                                    >
                                        {item.role}:{' '}
                                    </span>

                                    <span
                                        style={{
                                            fontWeight: 600,
                                        }}
                                    >
                                        {item.name}
                                    </span>
                                </div>
                            )
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Ngày chốt',
            dataIndex: 'selected_at',
            align: 'center',
            render: (value) =>
                value
                    ? moment(value).format(
                        'DD/MM/YYYY HH:mm'
                    )
                    : '',
        },
    ];

    /**
     * Phân trang bảng.
     */
    const handleTablePagination = (
        page,
        pageSize
    ) => {
        const nextQuery = {
            ...query,
            page,
            per_page: pageSize,
        };

        history.push({
            pathname: location.pathname,
            search: qs.stringify(
                cleanObject(nextQuery)
            ),
        });
    };

    const SummaryTitle = ({ label, tooltip }) => (
        <div className="summary-title">
            <span>{label}</span>

            {tooltip && (
                <Tooltip title={tooltip}>
                    <InfoCircleOutlined className="summary-title-icon" />
                </Tooltip>
            )}
        </div>
    );

    return (
        <div>
            <PageTitle
                routes={routes}
                title="Doanh số nhân viên chi tiết"
            />

            <div className="mt-2">
                <Title level={3}>
                    Báo cáo doanh số nhân viên chi tiết
                </Title>

                <div className="mt-2 mb-2">
                    <Row gutter={16}>
                        <Col xs={24} md={24}>
                            {/* ADMIN và KẾ TOÁN được chọn nhân viên */}
                            {showStaffSelect && (
                                <BaseSelect
                                    options={users}
                                    optionValue="user_id"
                                    optionLabel="display_name"
                                    placeholder="Chọn nhân viên"
                                    className="table-button"
                                    showSearch
                                    style={{
                                        width: 300,
                                    }}
                                    value={
                                        query.user_id
                                            ? Number(
                                                query.user_id
                                            )
                                            : undefined
                                    }
                                    onChange={(value) =>
                                        onChangeFilter(
                                            'user_id',
                                            value
                                        )
                                    }
                                />
                            )}

                            {/* Bộ lọc ngày */}
                            <RangePicker
                                format="DD/MM/YYYY"
                                allowClear={false}
                                className="table-button"
                                value={[
                                    query.start_date
                                        ? moment(
                                            query.start_date
                                        )
                                        : null,

                                    query.end_date
                                        ? moment(
                                            query.end_date
                                        )
                                        : null,
                                ]}
                                onChange={(value) =>
                                    onChangeFilter(
                                        'date',
                                        value
                                    )
                                }
                            />

                            {/* ==================== [BỔ SUNG] ==================== */}
                            {/* Bộ lọc tên, mã hoặc số điện thoại khách hàng */}
                            <Search
                                className="table-button"
                                style={{
                                    width: 300,
                                }}
                                allowClear
                                enterButton="Lọc"
                                placeholder="Tên, SĐT hoặc mã khách hàng"
                                value={customerKeyword}
                                onChange={
                                    handleCustomerKeywordChange
                                }
                                onSearch={
                                    handleCustomerSearch
                                }
                            />
                            {/* ================== [KẾT THÚC BỔ SUNG] ================== */}

                            {allowExport && (
                                <Button
                                    className="table-button"
                                    loading={
                                        isLoadingExport
                                    }
                                    type="default"
                                    onClick={
                                        startExcelExport
                                    }
                                    icon={
                                        <ExportOutlined />
                                    }
                                >
                                    Export
                                </Button>
                            )}

                            <Button
                                icon={<ReloadOutlined />}
                                className="table-button"
                                loading={isLoading}
                                onClick={loadData}
                            >
                                Refresh
                            </Button>
                        </Col>
                    </Row>
                </div>

                {/* Chưa sẵn sàng dữ liệu */}
                {!summary && !isLoading && (
                    <div
                        style={{
                            marginTop: 32,
                            textAlign: 'center',
                            opacity: 0.6,
                        }}
                    >
                        <Empty description="Vui lòng chọn nhân viên để xem báo cáo" />
                    </div>
                )}

                {/* Hiển thị kết quả */}
                {summary && (
                    <>
                        <div className="employee-summary-grid">
                            <Card>
                                <Statistic
                                    title={
                                        <SummaryTitle
                                            label="DS trước phân bổ"
                                            tooltip="Tổng giá trị các dịch vụ nhân viên tham gia trước khi chia đều cho số nhân viên trong từng dịch vụ."
                                        />
                                    }
                                    value={summary.total_original_revenue}
                                    formatter={(value) =>
                                        Number(value || 0).toLocaleString('vi-VN')
                                    }
                                    valueStyle={{ fontSize: 20 }}
                                />
                            </Card>

                            <Card>
                                <Statistic
                                    title={
                                        <SummaryTitle
                                            label="D/S nhân viên trong kỳ"
                                            tooltip="Tổng doanh số của các dịch vụ có ngày chốt nằm trong kỳ đang chọn, được chia đều cho các nhân viên tham gia từng dịch vụ."
                                        />
                                    }
                                    value={summary.total_revenue}
                                    formatter={(value) =>
                                        Number(value || 0).toLocaleString('vi-VN')
                                    }
                                    valueStyle={{ fontSize: 20 }}
                                />
                            </Card>

                            <Card>
                                <Statistic
                                    title={
                                        <SummaryTitle
                                            label="Tiền đã thu trong kỳ"
                                            tooltip="Tổng tiền từ các phiếu thu phát sinh trong khoảng thời gian lọc của các khách hàng xuất hiện trong báo cáo. Chưa phân bổ theo từng dịch vụ."
                                        />
                                    }
                                    value={summary.total_paid}
                                    formatter={(value) =>
                                        Number(value || 0).toLocaleString('vi-VN')
                                    }
                                    valueStyle={{ fontSize: 20 }}
                                />
                            </Card>

                            <Card>
                                <Statistic
                                    title={
                                        <SummaryTitle
                                            label="Công nợ cuối kỳ"
                                            tooltip="Tổng công nợ tại phiếu thu gần nhất tính đến ngày kết thúc kỳ của các khách hàng xuất hiện trong báo cáo."
                                        />
                                    }
                                    value={summary.total_debt}
                                    formatter={(value) =>
                                        Number(value || 0).toLocaleString('vi-VN')
                                    }
                                    valueStyle={{ fontSize: 20 }}
                                />
                            </Card>

                            <Card>
                                <Statistic
                                    title={
                                        <SummaryTitle
                                            label="Khách hàng"
                                            tooltip="Số khách hàng duy nhất có dịch vụ liên quan đến nhân viên hiện tại."
                                        />
                                    }
                                    value={summary.total_customers}
                                    valueStyle={{ fontSize: 20 }}
                                />
                            </Card>

                            <Card>
                                <Statistic
                                    title={
                                        <SummaryTitle
                                            label="Dịch vụ"
                                            tooltip="Số dịch vụ nhân viên hiện tại tham gia trong khoảng thời gian lọc."
                                        />
                                    }
                                    value={summary.total_services}
                                    valueStyle={{ fontSize: 20 }}
                                />
                            </Card>
                        </div>

                        <Table
                            size="small"
                            rowKey={(record) =>
                                record.service_id
                            }
                            columns={columns}
                            dataSource={detail || []}
                            loading={isLoading}
                            pagination={{
                                current:
                                    pagination?.currentPage ||
                                    pagination?.page ||
                                    1,

                                pageSize:
                                    pagination?.perPage ||
                                    pagination?.per_page ||
                                    30,

                                total:
                                    pagination?.total || 0,

                                pageSizeOptions: [
                                    '10',
                                    '20',
                                    '30',
                                    '40',
                                    '50',
                                    '100',
                                    '200',
                                ],

                                showSizeChanger: true,

                                showTotal: (total) =>
                                    `Tổng ${total} bản ghi`,

                                onChange:
                                    handleTablePagination,

                                onShowSizeChange:
                                    handleTablePagination,
                            }}
                            scroll={{
                                x: 'max-content',
                            }}
                        />
                    </>
                )}
            </div>
        </div>
    );
}